import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { trackEvent } from "../analytics.js";

/**
 * Favorites — a per-device set of station identifiers, one namespace
 * per station kind. Persisted in localStorage; on native the Capacitor
 * WebView shares localStorage with the page, so no plugin needed.
 *
 * Keys are the V2 Station `id` when present, and `name-<idx>` when the
 * source doesn't provide a stable id (matches the fallback key
 * `StationCard` already uses).
 */

export type FavoriteKind = "tv" | "radio";

interface FavoritesCtx {
  isFavorite: (kind: FavoriteKind, id: string) => boolean;
  toggle: (kind: FavoriteKind, id: string) => void;
  list: (kind: FavoriteKind) => string[];
}

const STORAGE_KEY = "orvis:favorites";
const Ctx = createContext<FavoritesCtx | undefined>(undefined);

interface Persisted {
  tv: string[];
  radio: string[];
}

function readPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tv: [], radio: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { tv: [], radio: [] };
    const p = parsed as Record<string, unknown>;
    return {
      tv: Array.isArray(p["tv"]) ? (p["tv"] as string[]).filter((s) => typeof s === "string") : [],
      radio: Array.isArray(p["radio"]) ? (p["radio"] as string[]).filter((s) => typeof s === "string") : [],
    };
  } catch {
    return { tv: [], radio: [] };
  }
}

function writePersisted(next: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private-mode / quota — silent, matches the country picker's tolerance. */
  }
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Persisted>(() => readPersisted());

  useEffect(() => writePersisted(state), [state]);

  const isFavorite = useCallback(
    (kind: FavoriteKind, id: string) => state[kind].includes(id),
    [state]
  );

  const toggle = useCallback((kind: FavoriteKind, id: string) => {
    setState((prev) => {
      const set = new Set(prev[kind]);
      const wasFavorite = set.has(id);
      if (wasFavorite) set.delete(id);
      else set.add(id);
      trackEvent("favorite_toggle", { kind, id, action: wasFavorite ? "remove" : "add" });
      return { ...prev, [kind]: Array.from(set) };
    });
  }, []);

  const list = useCallback((kind: FavoriteKind) => state[kind], [state]);

  const value = useMemo<FavoritesCtx>(
    () => ({ isFavorite, toggle, list }),
    [isFavorite, toggle, list]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFavorites(): FavoritesCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useFavorites must be used inside <FavoritesProvider>");
  return v;
}

/** Stable identifier for a Station across renders — mirrors what the list keys use. */
export function stationKey(station: { id?: string; name: string }, indexFallback = 0): string {
  return station.id ?? `${station.name}-${indexFallback}`;
}
